import type { RouteObject } from "react-router-dom";
import { Navigate, useLocation } from "react-router-dom";
import NotFound from "../pages/NotFound";
import Home from "../pages/home/page";
import PricingPage from "../pages/pricing/page";
import LoginPage from "../pages/auth/login/page";
import OnboardingPage from "../pages/onboarding/page";
import TermsPage from "../pages/legal/terms/page";
import PrivacyPage from "../pages/legal/privacy/page";
import RefundPage from "../pages/legal/refund/page";
import ShippingPage from "../pages/legal/shipping/page";
import ContactPage from "../pages/legal/contact/page";
import DashboardShell from "../components/feature/dashboard/DashboardShell";
import { RequireAuth } from "../lib/session";
import OverviewPage from "../pages/admin/overview/page";
import ReportsPage from "../pages/admin/reports/page";
import ReportBuilderPage from "../pages/admin/report-builder/page";
import OrdersPage from "../pages/admin/orders/page";
import MenuPage from "../pages/admin/menu/page";
import QrPage from "../pages/admin/qr/page";
import InventoryPage from "../pages/admin/inventory/page";
import ReservationsPage from "../pages/admin/reservations/page";
import CustomersPage from "../pages/admin/customers/page";
import StaffPage from "../pages/admin/staff/page";
import SettingsPage from "../pages/admin/settings/page";
import FeedbackAdminPage from "../pages/admin/feedback/page";
import AggregatorsPage from "../pages/admin/aggregators/page";
import OutletsPage from "../pages/admin/outlets/page";
import BillingPage from "../pages/admin/billing/page";
import RequireTier from "../components/feature/billing/RequireTier";
import SelfOrderPage from "../pages/self-order/page";
import FeedbackPage from "../pages/feedback/page";
import SuperadminLayout from "../pages/superadmin/components/Layout";
import SuperadminLogin from "../pages/superadmin/login/page";
import { AdminRequireAuth } from "../lib/superadmin";
import SuperadminDashboard from "../pages/superadmin/dashboard/page";
import SuperadminTenants from "../pages/superadmin/tenants/page";
import SuperadminTenantDetail from "../pages/superadmin/tenants/detail/page";
import SuperadminMenu from "../pages/superadmin/menu/page";
import SuperadminBilling from "../pages/superadmin/billing/page";
import SuperadminEntitlements from "../pages/superadmin/entitlements/page";
import SuperadminSystem from "../pages/superadmin/system/page";
import SuperadminAnalytics from "../pages/superadmin/analytics/page";
import SuperadminFinance from "../pages/superadmin/finance/page";
import SuperadminVoided from "../pages/superadmin/voided/page";
import SuperadminPlans from "../pages/superadmin/plans/page";
import SuperadminCoupons from "../pages/superadmin/coupons/page";
import SuperadminAnnouncements from "../pages/superadmin/announcements/page";

/** Redirects any legacy /admin or /admin/* URL to the matching /dashboard path,
    preserving the sub-path and query string. */
function AdminRedirect() {
  const loc = useLocation();
  const to = loc.pathname.replace(/^\/admin/, "/dashboard") + loc.search;
  return <Navigate to={to} replace />;
}

const routes: RouteObject[] = [
  // Public marketing + auth surfaces
  { path: "/", element: <Home /> },
  { path: "/pricing", element: <PricingPage /> },
  { path: "/login", element: <LoginPage /> },
  { path: "/onboarding", element: <OnboardingPage /> },

  // Legal / policy pages (required for payment-gateway approval). These are
  // static and must precede the "/:outletSlug" catch-all so they aren't treated
  // as restaurant slugs.
  { path: "/terms", element: <TermsPage /> },
  { path: "/privacy-policy", element: <PrivacyPage /> },
  { path: "/refund-policy", element: <RefundPage /> },
  { path: "/shipping-policy", element: <ShippingPage /> },
  { path: "/contact", element: <ContactPage /> },

  // Restaurant owner/admin dashboard
  {
    path: "/dashboard",
    element: (
      <RequireAuth>
        <DashboardShell />
      </RequireAuth>
    ),
    children: [
      { index: true, element: <OverviewPage /> },
      { path: "reports", element: <ReportsPage /> },
      { path: "report-builder", element: <RequireTier feature="report-builder"><ReportBuilderPage /></RequireTier> },
      { path: "orders", element: <OrdersPage /> },
      { path: "menu", element: <MenuPage /> },
      { path: "qr", element: <QrPage /> },
      { path: "reservations", element: <RequireTier feature="reservations"><ReservationsPage /></RequireTier> },
      { path: "inventory", element: <RequireTier feature="inventory"><InventoryPage /></RequireTier> },
      { path: "customers", element: <RequireTier feature="customers"><CustomersPage /></RequireTier> },
      { path: "feedback", element: <RequireTier feature="feedback"><FeedbackAdminPage /></RequireTier> },
      { path: "aggregators", element: <RequireTier feature="aggregators"><AggregatorsPage /></RequireTier> },
      { path: "outlets", element: <RequireTier feature="outlets"><OutletsPage /></RequireTier> },
      { path: "staff", element: <StaffPage /> },
      { path: "settings", element: <SettingsPage /> },
      { path: "billing", element: <BillingPage /> },
    ],
  },

  // Back-compat: old /admin/* links/bookmarks now redirect to /dashboard/*
  // (the splat also matches bare /admin). Must precede the "/:outletSlug"
  // catch-all so "admin" isn't treated as a restaurant slug.
  { path: "/admin/*", element: <AdminRedirect /> },

  // Platform-owner admin login (public — separate from merchant /login)
  { path: "/superadmin/login", element: <SuperadminLogin /> },

  // Platform-owner superadmin panel (gated by AdminRequireAuth)
  {
    path: "/superadmin",
    element: (
      <AdminRequireAuth>
        <SuperadminLayout />
      </AdminRequireAuth>
    ),
    children: [
      { index: true, element: <SuperadminDashboard /> },
      { path: "dashboard", element: <SuperadminDashboard /> },
      { path: "tenants", element: <SuperadminTenants /> },
      { path: "tenants/:id", element: <SuperadminTenantDetail /> },
      { path: "menu", element: <SuperadminMenu /> },
      { path: "billing", element: <SuperadminBilling /> },
      { path: "plans", element: <SuperadminPlans /> },
      { path: "entitlements", element: <SuperadminEntitlements /> },
      { path: "coupons", element: <SuperadminCoupons /> },
      { path: "announcements", element: <SuperadminAnnouncements /> },
      { path: "analytics", element: <SuperadminAnalytics /> },
      { path: "finance", element: <SuperadminFinance /> },
      { path: "voided", element: <SuperadminVoided /> },
      { path: "system", element: <SuperadminSystem /> },
    ],
  },

  // Public feedback form (QR / link). Must precede the "/:outletSlug" catch-all.
  {
    path: "/feedback/:outletSlug",
    element: <FeedbackPage />,
  },

  // Customer-facing QR self-order
  {
    path: "/t/:outletSlug/:tableCode",
    element: <SelfOrderPage />,
  },

  // Public restaurant link (browse menu, no table) — shareable merchant URL
  // Old /r/{slug} form kept for backward compatibility.
  {
    path: "/r/:outletSlug",
    element: <SelfOrderPage />,
  },

  // Root-level restaurant link: ezydinepro.com/{slug}
  // Static routes above (/login, /admin, /t/...) always win over this
  // dynamic segment; slug generation also avoids reserved words.
  {
    path: "/:outletSlug",
    element: <SelfOrderPage />,
  },

  { path: "*", element: <NotFound /> },
];

export default routes;
